/**
 * @fileoverview Source context utilities for explaining data origins.
 * @module features/sku-alias/utils/sourceContext
 */

import { formatMoney } from '@/shared/utils/money'

/**
 * Source information including display name, description, and impact type.
 */
export interface SourceInfo {
  /** Human-readable display name */
  displayName: string
  /** Detailed description of what this source represents */
  description: string
  /** Type of financial impact this source typically provides */
  impactType: 'revenue' | 'adSpend' | 'mixed'
  /** Color scheme for UI elements */
  color: string
}

/**
 * Source configuration mapping.
 */
const SOURCE_CONFIG: Record<string, SourceInfo> = {
  canonical: {
    displayName: 'Direct match',
    description: 'Primary authoritative name for the variant',
    impactType: 'mixed',
    color: 'purple',
  },
  baselinker: {
    displayName: 'BaseLinker',
    description: 'Sales data from e-commerce platform integration',
    impactType: 'revenue',
    color: 'green',
  },
  facebook_ads: {
    displayName: 'Facebook Ads',
    description: 'Advertising campaign data from Meta',
    impactType: 'adSpend',
    color: 'blue',
  },
  google_ads: {
    displayName: 'Google Ads',
    description: 'Advertising campaign data from Google',
    impactType: 'adSpend',
    color: 'orange',
  },
  sales_csv: {
    displayName: 'Sales CSV',
    description: 'Manual sales data import from CSV file',
    impactType: 'revenue',
    color: 'emerald',
  },
  meta_ads_csv: {
    displayName: 'Meta Ads CSV',
    description: 'Manual advertising data import from CSV file',
    impactType: 'adSpend',
    color: 'indigo',
  },
  manual: {
    displayName: 'Manual Entry',
    description: 'Manually entered SKU data',
    impactType: 'mixed',
    color: 'purple',
  },
  api: {
    displayName: 'API',
    description: 'Data from external API integration',
    impactType: 'mixed',
    color: 'cyan',
  },
}

/**
 * Get source information for a given source identifier.
 *
 * @param source - Source identifier string
 * @returns Source information object
 *
 * @example
 * ```typescript
 * const info = getSourceInfo('baselinker')
 * // returns: {
 * //   displayName: 'BaseLinker',
 * //   description: 'Sales data from e-commerce platform integration',
 * //   impactType: 'revenue',
 * //   color: 'green'
 * // }
 * ```
 */
export function getSourceInfo(source: string): SourceInfo {
  return (
    SOURCE_CONFIG[source] || {
      displayName: source.charAt(0).toUpperCase() + source.slice(1),
      description: 'Unknown data source',
      impactType: 'mixed',
      color: 'gray',
    }
  )
}

/**
 * Get contextual explanation for what the financial impact means based on source.
 *
 * @param source - Source identifier string
 * @param revenue - Unattributed revenue amount
 * @param adSpend - Unattributed ad spend amount
 * @returns Contextual explanation string
 *
 * @example
 * ```typescript
 * const explanation = getImpactExplanation('baselinker', 45230.50, 0)
 * // returns: '€45,230.50 in lost sales revenue from untracked products'
 * ```
 */
export function getImpactExplanation(
  source: string,
  totalAmount: number,
  currency?: string | null
): string {
  const info = getSourceInfo(source)
  const currencyCode = currency ?? 'EUR'
  const formatted = formatMoney(totalAmount, currencyCode, 'en-US', 0, 2, 'narrowSymbol')

  if (totalAmount === 0) {
    return 'No financial impact detected yet'
  }

  if (info.impactType === 'revenue') {
    return `${formatted} in lost sales revenue from untracked products`
  }

  if (info.impactType === 'adSpend') {
    return `${formatted} in wasted advertising spend`
  }

  return `${formatted} unmapped financial impact`
}

/**
 * Get tooltip content explaining the source and its impact.
 *
 * @param source - Source identifier string
 * @param revenue - Unattributed revenue amount
 * @param adSpend - Unattributed ad spend amount
 * @returns Tooltip content string
 *
 * @example
 * ```typescript
 * const tooltip = getSourceTooltip('baselinker', 45230.50, 0)
 * // returns: 'BaseLinker: Sales data from e-commerce platform\nImpact: €45,230.50 lost revenue'
 * ```
 */
export function getSourceTooltip(
  source: string,
  totalAmount: number,
  currency?: string | null
): string {
  const info = getSourceInfo(source)
  const impact = getImpactExplanation(source, totalAmount, currency)

  return `${info.displayName}: ${info.description}\nImpact: ${impact}`
}

/**
 * Check if a source typically provides revenue data.
 *
 * @param source - Source identifier string
 * @returns True if source typically provides revenue data
 *
 * @example
 * ```typescript
 * isRevenueSource('baselinker') // true
 * isRevenueSource('facebook_ads') // false
 * ```
 */
export function isRevenueSource(source: string): boolean {
  const info = getSourceInfo(source)
  return info.impactType === 'revenue' || info.impactType === 'mixed'
}

/**
 * Check if a source typically provides ad spend data.
 *
 * @param source - Source identifier string
 * @returns True if source typically provides ad spend data
 *
 * @example
 * ```typescript
 * isAdSpendSource('facebook_ads') // true
 * isAdSpendSource('baselinker') // false
 * ```
 */
export function isAdSpendSource(source: string): boolean {
  const info = getSourceInfo(source)
  return info.impactType === 'adSpend' || info.impactType === 'mixed'
}
